import { gql, useLazyQuery } from '@apollo/client';
import Select from '@orn/shared-web/components/Select';
import * as React from 'react';

const ADMIN_LIST_MERCHANTS = gql`
  query AdminListMerchants($userId: ID!) {
    adminListMerchants(userId: $userId) {
      id
      name
    }
  }
`;

interface IAdminListMerchantsQuery {
  adminListMerchants: Array<{
    id: string;
    name: string | null;
  }>;
}

interface IAdminListMerchantsQueryVariables {
  userId: string;
}

interface IAdminSelectMerchantProps {
  selectedUserId: string | null;
  selectedMerchantId: string | null;
  onSelectionChange: (merchantId: string | null, merchantName?: string | null) => void;
  label?: string;
  className?: string;
}

const AdminSelectMerchant = ({
  selectedUserId,
  selectedMerchantId,
  onSelectionChange,
  label = 'Merchant',
  className,
}: IAdminSelectMerchantProps) => {
  const [loadMerchants, { data: merchantsData, loading: merchantsLoading }] = useLazyQuery<
    IAdminListMerchantsQuery,
    IAdminListMerchantsQueryVariables
  >(ADMIN_LIST_MERCHANTS);
  const previousSelectedUserIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!selectedUserId) {
      previousSelectedUserIdRef.current = null;
      onSelectionChange(null, null);
      return;
    }

    if (previousSelectedUserIdRef.current != null && previousSelectedUserIdRef.current !== selectedUserId) {
      onSelectionChange(null, null);
    }

    previousSelectedUserIdRef.current = selectedUserId;
    void loadMerchants({ variables: { userId: selectedUserId } });
  }, [selectedUserId, loadMerchants, onSelectionChange]);

  const merchants = React.useMemo(() => {
    if (!selectedUserId) {
      return [];
    }

    const merchantItems =
      merchantsData?.adminListMerchants.map((merchant) => ({
        id: merchant.id,
        label: merchant.name || `Merchant ${merchant.id}`,
      })) ?? [];

    if (merchantItems.length === 0) {
      return [{ id: 'no-merchants-found', label: 'No items found', isDisabled: true }];
    }

    return merchantItems;
  }, [selectedUserId, merchantsData?.adminListMerchants]);

  return (
    <Select
      label={label}
      placeholder={selectedUserId ? 'Select a merchant...' : 'Select a user first'}
      value={selectedMerchantId || null}
      onChange={(key) => {
        const nextMerchantId = key ? (key as string) : null;
        if (!nextMerchantId || nextMerchantId === 'no-merchants-found') {
          onSelectionChange(null, null);
          return;
        }

        const selectedMerchant = merchantsData?.adminListMerchants.find((merchant) => merchant.id === nextMerchantId);
        const selectedMerchantName = selectedMerchant?.name || `Merchant ${nextMerchantId}`;

        onSelectionChange(nextMerchantId, selectedMerchantName);
      }}
      isDisabled={!selectedUserId || merchantsLoading}
      items={merchants}
      className={className}
    >
      {(item) => (
        <Select.Item id={item.id} isDisabled={item.isDisabled}>
          {item.label}
        </Select.Item>
      )}
    </Select>
  );
};

export default AdminSelectMerchant;
